// Style Imports
import Styles from './Sidebar.module.scss';

// React Imports
import { useEffect, useState } from "react";

// Component Imports
import FileTree from './FileTree/FileTree';
import { getFileIcon } from '../filetypemapping';
import AlertDialog from '../../AlertDialog/AlertDialog';

// Module Imports 
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";

// Context Imports
import { useEditor } from '../../../contexts';
import { useMultiTabEditor } from '../../../contexts';

import Tooltip from '@mui/material/Tooltip';

const Sidebar = () => {

    const { socket, rootPath } = useEditor();
    const { fileTree, setFileTree, handleFileClick: handleContextFileClick, removeTab, selectedItem, setSelectedItem } = useMultiTabEditor()
    const [rootFolder, setRootFolder] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogConfig, setDialogConfig] = useState({
        title: "",
        content: "",
        confirmText: "",
        cancelText: "",
        confirmIcon: null,
        cancelIcon: null,
        onConfirm: null,
        onCancel: null,
        isForm: false,
        formDetails: {
            name: "",
            label: "",
            value: ""
        }
    });
     // Context menu states
     const [contextMenu, setContextMenu] = useState(null);
     const [rightClickedItem, setRightClickedItem] = useState(null);
     
    const { setUserFiles } = useEditor();

    //Initialize function to set file tree
    const initializeFileTree = () => {
        socket.emit('root:fetch', { rootPath: rootPath });
        socket.once('root:data', (rootData) => {
            const root = rootData[0];
            setRootFolder(root);
            setSelectedItem(root);
            setFileTree(root?.children || []);
        });
    };

    // File system event handling setup
    const handleAddEvent = (entry) => {
        if (entry.type === 'file') {
            setUserFiles(prev => ({ ...prev, [entry.path]: '' }));
        }

        if (entry.parentPath === rootPath) {
            setRootFolder(prev => ({
                ...prev,
                children: sortChildren([...(prev.children || []), entry])
            }));
            setFileTree(prev => sortChildren([...prev, entry]));
        } else {
            setFileTree(prevTree => updateTree(prevTree, entry.parentPath, [entry]));
        }
    };

    const handleDeleteEvent = (entry) => {
        if (entry.parentPath === rootPath) {
            setRootFolder(prev => ({
                ...prev,
                children: prev.children.filter(child => child.path !== entry.path)
            }));
            setFileTree(prev => prev.filter(item => item.path !== entry.path));
        } else {
            setFileTree(prevTree => removeNode(prevTree, entry.path));
        }

        if (selectedItem?.path === entry.path) {
            setSelectedItem(rootFolder);
        }
        // removeTab(entry.path)
        setUserFiles(prev => {
            const updatedFiles = { ...prev };
            delete updatedFiles[entry.path];
            return updatedFiles;
        });
    };

    const handleFileSystemEvent = ({ eventType, entry }) => {

        switch (eventType) {
            case 'add':
            case 'addDir':
                handleAddEvent(entry);
                break;
            case 'unlink':
            case 'unlinkDir':
                handleDeleteEvent(entry);
                break;
            case 'change':
                break;
            default:
                break;
        }
    };


    useEffect(() => {
        // Initialize and set up event listeners
        initializeFileTree();
        socket.on("tree:update", handleFileSystemEvent);
        socket.once('tree:refresh:success', (rootData) => {
            const root = rootData[0];
            setRootFolder(root);
            setSelectedItem(root);
            setFileTree(root?.children || []);
        });

        // Cleanup
        return () => {
            socket.off('tree:data');
            socket.off('root:data');
            socket.off('tree:refresh:success');
        };
    }, [socket]);

    const sortChildren = (children) => {
        return children.sort((a, b) => {
            // Folders come before files
            if (a.type === "folder" && b.type !== "folder") return -1;
            if (a.type !== "folder" && b.type === "folder") return 1;

            // Alphabetical order
            return a.name.localeCompare(b.name);
        });
    };

    const updateTree = (tree, targetPath, children) =>
        tree.map((node) => {
            if (node.path === targetPath) {
                const updatedChildren = sortChildren([...(node.children || []), ...children])
                return { ...node, isLoaded: true, children: updatedChildren };
            }
            if (node.type === "folder" && node.children.length) {
                return { ...node, children: updateTree(node.children, targetPath, children) };
            }
            return node;
        });

    const handleFolderToggle = (folder) => {
        setExpandedFolders((prevExpandedFolders) => {
            const newExpandedFolders = new Set(prevExpandedFolders);
            if (newExpandedFolders.has(folder.path)) {
                newExpandedFolders.delete(folder.path); // Collapse folder
            } else {
                newExpandedFolders.add(folder.path); // Expand folder
            }
            return newExpandedFolders;
        });
    }

    const handleFolderExpand = (folder) => {

        setSelectedItem(folder)
        handleFolderToggle(folder)

        if (folder.isLoaded) return;
        socket.emit("dir:fetch", { parentPath: folder.path });

        socket.once("dir:data", (data) => {
            if (folder.path === rootPath) {
                setRootFolder(prev => ({
                    ...prev,
                    children: data,
                    isLoaded: true
                }));
                setFileTree(data);
            } else {
                setFileTree((prevTree) => updateTree(prevTree, folder.path, data));
            }
        });
    };

    const handleFileClick = (file) => {

        setSelectedItem(file);

        if (!file.content) {
            socket.emit('file:fetch', { filePath: file.path });

            socket.once('file:content', (content) => {
                setFileTree((prevTree) => updateFileContent(prevTree, file.path, content));
                file.content = content;
                handleContextFileClick(file);

            });
        } else {
            handleContextFileClick(file);
        }
    }

    const updateFileContent = (tree, path, content) => {
        return tree.map((node) => {
            if (node.path === path) {
                return { ...node, content }; // Add content to the file object
            }
            if (node.type === 'folder' && node.children.length) {
                return { ...node, children: updateFileContent(node.children, path, content) };
            }
            return node;
        });
    };

    // const findNodeByPath = (tree, path) => {
    //     for (let node of tree) {
    //         if (node.path === path) {
    //             return node;
    //         }
    //         if (node.type === "folder" && node.children?.length) {
    //             const result = findNodeByPath(node.children, path);
    //             if (result) return result;
    //         }
    //     }
    //     return null;
    // };

    const openDialog = (title, content, confirmText = "", cancelText = "", onConfirm = null, onCancel = null, confirmIcon = null, cancelIcon = null, isForm = false, formDetails) => {
        setDialogConfig({ title, content, confirmText, cancelText, onConfirm, onCancel, confirmIcon, cancelIcon, isForm, formDetails });
        setDialogOpen(true);
    };

    const handleAddFileOrFolder = (type, node) => {
        // Use selected item or root folder if nothing selected
        const targetFolder = node?.type === 'folder' ? node : rootFolder;

        if (!targetFolder) return;

        openDialog(
            `Create ${type}`,
            `Enter ${type} name:`,
            "Add",
            "Cancel",
            (inputValue) => {
                if (!inputValue) return

                // Check the correct children array based on the target folder
                const children = targetFolder.path === rootPath ?
                    rootFolder.children :
                    targetFolder.children;

                if (children?.some((child) => child.name === inputValue)) {
                    openDialog("File Exists", `A ${type} with the name "${inputValue}" already exists in this folder.`, "Confirm", null, () => { setDialogOpen(false) })
                    return;
                }
                const event = type === "file" ? "file:add" : "dir:add";
                socket.emit(event, { parentPath: targetFolder.path, name: inputValue });

                socket.once(`${event}:success`, (data) => {
                    // console.log("File or folder created successfully");
                });
                setDialogOpen(false);
            },
            () => { setDialogOpen(false) },
            null,
            null,
            true,
            { name: `${type}`, label: `${type} name` }
        )
    };

    const removeNode = (tree, targetPath) => {
        return tree.filter((node) => {
            if (node.path === targetPath) {
                return false; // Remove the target node
            }
            if (node.type === "folder" && node.children?.length) {
                node.children = removeNode(node.children, targetPath); // Recursively process children
            }
            return true; // Keep other nodes
        });
    };

    const handleDelete = (node) => {
        openDialog(
            `Delete ${node.name}`,
            `Are you sure you want to delete ${node.name}?`,
            "Delete",
            "Cancel",
            (confirmed) => {
                if (confirmed) {
                    const event = node.type === "file" ? "file:delete" : "dir:delete";
                    socket.emit(event, { path: node.path });

                    socket.once(`${event}:success`, () => {
                        setSelectedItem(rootFolder)
                        removeTab(node.path)
                    });
                }
                setDialogOpen(false);
            },
            () => {
                setDialogOpen(false);
            },
        )
    };

    const handleRename = (node) => {
        openDialog(
            "Rename File or Folder",
            `Enter the new name for "${node.name}":`,
            "Rename",
            "Cancel",
            (newName) => {
                if (!newName || newName.trim() === node.name) return;

                const newPath = `${node.path.split("/").slice(0, -1).join("/")}/${newName.trim()}`;

                socket.emit("tree:rename", { oldPath: node.path, newPath });

                socket.once("tree:rename:success", () => {
                });

                socket.once("tree:rename:failure", (error) => {
                    console.error(`Failed to rename: ${error.message}`);
                    openDialog("Rename Failed", `Failed to rename ${node.name}`, 'Confirm', null, () => { setDialogOpen(false) })
                    alert(`Failed to rename: ${error.message}`);
                });

                setDialogOpen(false)
            },
            () => {
                setDialogOpen(false)
            },
            null,
            null,
            true, // Enable input field
            { label: "New Name", name: "new", value: `${node.name}` }
        );
    };

    const handleBackgroundClick = (e) => {
        if (e.target.id === 'fileTreeContainer' && rootFolder) {
            setSelectedItem(rootFolder);
        }
    };

    // Context menu handlers
    const handleContextMenu = (event, item) => {
        event.preventDefault();
        event.stopPropagation();
        setRightClickedItem(item);
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
        });
        setSelectedItem(item);
    };

    const handleContextMenuClose = () => {
        setContextMenu(null);
    };

    // Pass the context menu handler to FileTree
    const handleFileTreeContextMenu = (event) => {
        if (event.target.id === 'fileTreeContainer' && rootFolder) {
            handleContextMenu(event, rootFolder);
        }
    };

    return (
        <div
            id="fileTreeContainer"
            className={Styles.fileTreeContainer}
            onClick={handleBackgroundClick}
            onContextMenu={handleFileTreeContextMenu}
        >
            <div
                id="treeHeader"
                className={Styles.treeHeader}
            >
                {/* <span style={{ marginLeft: '0.8em' }}>{rootFolder?.name.toUpperCase()}</span> */}
                <div id='headerButtons' className={Styles.headerButtons}>
                    <Tooltip title="New File">
                    <span>
                    <IconButton
                        id='addFileButton'
                        size="small"
                        onClick={() => handleAddFileOrFolder("file", selectedItem)}
                        disabled={!selectedItem?.path || (selectedItem?.type !== 'folder' && selectedItem?.path !== rootFolder?.path)}
                    >
                        {getFileIcon('addfile')}
                    </IconButton>
                    </span>
                    </Tooltip>
                    
                    <Tooltip title="New Folder">
                    <span>
                    <IconButton
                        id='addFolderButton'
                        size="small"
                        onClick={() => handleAddFileOrFolder("folder", selectedItem)}
                        disabled={!selectedItem?.path || (selectedItem?.type !== 'folder' && selectedItem?.path !== rootFolder?.path)}
                    >
                        {getFileIcon('addfolder')}
                    </IconButton>
                    </span>
                    </Tooltip>

                    <Tooltip title="Rename">
                    <span>
                    <IconButton
                        id='renameButton'
                        size="small"
                        onClick={() => handleRename(selectedItem)}
                        disabled={!selectedItem?.path || selectedItem?.type === 'folder' || selectedItem?.path === rootFolder?.path}
                    >
                        {getFileIcon('edit')}
                    </IconButton>
                    </span>
                    </Tooltip>
                    
                    <Tooltip title="Delete">
                    <span>
                    <IconButton
                        id='deleteButton'
                        size="small"
                        onClick={() => handleDelete(selectedItem)}
                        disabled={!selectedItem?.path || selectedItem?.path === rootFolder?.path}
                    >
                        {getFileIcon('delete')}
                    </IconButton>
                    </span>
                    </Tooltip>
                </div>
            </div>
            <FileTree
                fileTree={fileTree}
                onFolderExpand={handleFolderExpand}
                onFileClick={handleFileClick}
                selectedItem={selectedItem}
                expandedFolders={expandedFolders}
                rootFolder={rootFolder}
                onContextMenu={handleContextMenu}
            />
            <AlertDialog
                dialogOpen={dialogOpen}
                dialogTitle={dialogConfig.title}
                dialogText={dialogConfig.content}
                onConfirm={dialogConfig.onConfirm}
                onCancel={dialogConfig.onCancel}
                confirmButtonText={dialogConfig.confirmText}
                cancelButtonText={dialogConfig.cancelText}
                isForm={dialogConfig.isForm}
                formDetails={dialogConfig.formDetails}
            />
            
            {/* Context Menu */}
            <Menu
                open={contextMenu !== null}
                onClose={handleContextMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                {(rightClickedItem?.type === 'folder' || rightClickedItem?.path === rootFolder?.path) && (
                    <MenuItem onClick={() => {
                        handleAddFileOrFolder("file", rightClickedItem);
                        handleContextMenuClose();
                    }}>
                        <ListItemIcon>
                            {getFileIcon('addfile')}
                        </ListItemIcon>
                        <ListItemText>Add File</ListItemText>
                    </MenuItem>
                )}
                
                {(rightClickedItem?.type === 'folder' || rightClickedItem?.path === rootFolder?.path) && (
                    <MenuItem onClick={() => {
                        handleAddFileOrFolder("folder", rightClickedItem);
                        handleContextMenuClose();
                    }}>
                        <ListItemIcon>
                            {getFileIcon('addfolder')}
                        </ListItemIcon>
                        <ListItemText>Add Folder</ListItemText>
                    </MenuItem>
                )}
                
                {rightClickedItem?.path !== rootFolder?.path && rightClickedItem?.type !== 'folder' && (
                    <MenuItem onClick={() => {
                        handleRename(rightClickedItem);
                        handleContextMenuClose();
                    }}>
                        <ListItemIcon>
                            {getFileIcon('edit')}
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                )}
                
                {rightClickedItem?.path !== rootFolder?.path && (
                    <MenuItem onClick={() => {
                        handleDelete(rightClickedItem);
                        handleContextMenuClose();
                    }}>
                        <ListItemIcon>
                            {getFileIcon('delete')}
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </div>
    )
}

export default Sidebar;